import React, { useMemo } from 'react';
import { Chip, Stack } from '@mui/material';
import FitnessCenterIcon from '@mui/icons-material/FitnessCenter';
import EditIcon from '@mui/icons-material/Edit';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import SyncIcon from '@mui/icons-material/Sync';
import ViewListIcon from '@mui/icons-material/ViewList';
import { alpha } from '@mui/material/styles';
import { useResources } from '@platform/core/src/context/ResourceContext';

const formatMeasurements = (actions) => {
  if (!actions || !Array.isArray(actions) || actions.length === 0) return '';
  const summaryParts = [];
  
  // Usually in a plan we just look at the first action to describe the prescription
  const action = actions[0];
  const measurements = action.measurements || action.prescribed_measurements || [];
  
  const reps = measurements.find(m => m.type === 'REPETITIONS' || m.type === 'reps');
  const weight = measurements.find(m => m.type === 'WEIGHT' || m.type === 'weight');
  const duration = measurements.find(m => m.type === 'DURATION' || m.type === 'duration');
  const distance = measurements.find(m => m.type === 'DISTANCE' || m.type === 'distance');

  const parts = [];
  // For plans, we often want to show set count
  if (actions.length > 1) parts.push(`${actions.length} sets`);
  
  if (reps && reps.target_value) parts.push(`${reps.target_value} reps`);
  if (weight && weight.target_value) parts.push(`${weight.target_value} ${weight.target_unit || 'lbs'}`);
  if (duration && duration.target_value) parts.push(`${duration.target_value} ${duration.target_unit || 'min'}`);
  
  return parts.join(', ');
};

export const getWorkoutPlanChanges = (event, plan, allDefinitions = []) => {
    const { patches, change_type, resource_id } = event;
    const changes = [];

    if (change_type === 'created') {
        changes.push({
            id: `created-${resource_id}`,
            label: "New Workout Plan Created",
            icon: <AddCircleOutlineIcon fontSize="small" />,
            color: "primary"
        });
        return changes;
    }

    if (change_type === 'updated' && (!patches || patches.length === 0)) {
         changes.push({
            id: `updated-${resource_id}-${Date.now()}`,
            label: "Plan Synced",
            icon: <SyncIcon fontSize="small" />,
            color: "default"
        });
        return changes;
    }

    if (!patches || !Array.isArray(patches)) return [];

    const resolveActivityName = (activityObj) => {
      if (!activityObj) return 'Activity';
      if (activityObj.name) return activityObj.name;
      if (activityObj.exercise_definition_id) {
          const def = allDefinitions.find(d => String(d.id) === String(activityObj.exercise_definition_id));
          if (def) return def.name;
      }
      return 'Activity';
    };

    patches.forEach((patch) => {
      const path = patch.path;

      // 1. Handle Stage Operations
      if (patch.op === 'add' && /^\/stages\/(\d+|-)$/.test(path)) {
          const stage = patch.value;
          changes.push({
              id: `add-stage-${path}`,
              label: `Added Stage: ${stage.name || 'New Stage'}`,
              icon: <ViewListIcon fontSize="small" />,
              color: "primary"
          });
          return;
      }

      // 2. Handle Activity Operations (Nested in Stages)
      // Matches: /stages/0/activities/- or /stages/1/activities/2
      const activityMatch = path.match(/^\/stages\/(\d+)\/activities\/(\d+|-)$/);
      
      if (patch.op === 'add' && activityMatch) {
          const stageIndex = parseInt(activityMatch[1], 10);
          const activity = patch.value;
          const activityName = resolveActivityName(activity);
          const details = formatMeasurements(activity.actions);
          
          // Try to find stage name if available in current plan state
          const stageName = plan?.stages?.[stageIndex]?.name 
            ? ` to ${plan.stages[stageIndex].name}` 
            : ` to Stage ${stageIndex + 1}`;

          changes.push({
              id: `add-act-${path}`,
              label: `Added: ${activityName}${stageName}${details ? ` (${details})` : ''}`,
              icon: <FitnessCenterIcon fontSize="small" />,
              color: "success"
          });
          return;
      }

      if (patch.op === 'remove' && activityMatch) {
          changes.push({
              id: `rem-act-${path}`,
              label: "Removed Activity",
              icon: <RemoveCircleOutlineIcon fontSize="small" />,
              color: "default"
          });
          return;
      }

      // 3. Handle Set/Action Operations
      // Matches: /stages/0/activities/0/actions/-
      const setMatch = path.match(/^\/stages\/(\d+)\/activities\/(\d+)\/actions\/(\d+|-)$/);
      if (patch.op === 'add' && setMatch) {
          const stageIndex = parseInt(setMatch[1], 10);
          const activityIndex = parseInt(setMatch[2], 10);
          
          let activityName = 'Activity';
          if (plan?.stages?.[stageIndex]?.activities?.[activityIndex]) {
              activityName = resolveActivityName(plan.stages[stageIndex].activities[activityIndex]);
          }

          changes.push({
              id: `add-set-${path}`,
              label: `Added Set to ${activityName}`,
              icon: <FitnessCenterIcon fontSize="small" />,
              color: "info"
          });
          return;
      }

      // 4. Handle Updates (Replacements)
      if (patch.op === 'replace') {
          if (path.endsWith('/name') && path.includes('/activities/')) {
              changes.push({
                  id: `upd-name-${path}`,
                  label: `Renamed Activity to: ${patch.value}`,
                  icon: <EditIcon fontSize="small" />,
                  color: "default"
              });
          }
          else if (path.includes('/target_value') || path.includes('/prescribed_measurements')) {
               // Debounce generic updates so we don't show 5 chips for 5 field updates
               const exists = changes.find(c => c.id === 'generic-update');
               if (!exists) {
                   changes.push({
                       id: 'generic-update',
                       label: `Updated Plan Details`,
                       icon: <EditIcon fontSize="small" />,
                       color: "default"
                   });
               }
          }
      }
    });

    return changes;
};

const WorkoutPlanEffectRenderer = ({ event }) => {
  const { resource_id } = event;
  const { getResourceById, getResourcesByType } = useResources();
  const allDefinitions = useMemo(() => getResourcesByType('exercise_definition'), [getResourcesByType]);
  const plan = getResourceById(resource_id);

  const changesToRender = useMemo(() => {
      return getWorkoutPlanChanges(event, plan, allDefinitions);
  }, [event, plan, allDefinitions]);

  if (changesToRender.length === 0) return null;

  return (
    <Stack direction="column" spacing={0.5} sx={{ width: '100%', my: 0.5 }}>
        {changesToRender.map((change) => {
            const getColor = (theme) => {
                if (!change.color || change.color === 'default') {
                    return theme.palette.text.secondary;
                }
                return theme.palette[change.color]?.main || theme.palette.primary.main;
            };

            return (
            <Chip
                key={change.id}
                icon={change.icon}
                label={change.label}
                size="small"
                variant="outlined"
                color={change.color === 'default' ? 'default' : (change.color || 'default')}
                sx={{
                    maxWidth: '100%',
                    justifyContent: 'flex-start',
                    bgcolor: (theme) => alpha(getColor(theme), 0.05),
                    borderColor: (theme) => alpha(getColor(theme), 0.3),
                    '& .MuiChip-label': {
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        fontWeight: 500
                    }
                }}
            />
            );
        })}
    </Stack>
  );
};

WorkoutPlanEffectRenderer.shouldRender = (event) => {
    const changes = getWorkoutPlanChanges(event, null, []);
    return changes.length > 0;
};

export default WorkoutPlanEffectRenderer;